"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import {
  Smartphone,
  Send,
  Users,
  Filter,
  Calendar,
  BarChart3,
  Wallet,
  Sparkles,
  FileSpreadsheet,
  Tag,
  Globe,
  Clock,
  ArrowLeft,
  PlayCircle,
  Target,
  TrendingUp,
  ShieldCheck,
  Zap,
} from "lucide-react";
import SiteHeader from "@/components/landing/SiteHeader";
import SiteFooter from "@/components/landing/SiteFooter";
import {
  SolutionShell,
  Orbs,
  GridBg,
  Section,
  SectionHeader,
  GiantStats,
  LineFeatures,
  SplitFeature,
  StepsTimeline,
  CompareSplit,
  BigQuote,
  MiniTestimonials,
  FAQ,
  FinalCTA,
  HeroBadge,
  HeroTitle,
  PricingStrip,
} from "@/components/landing/solutions/SolutionSections";

function Page() {
  return (
    <SolutionShell>
      <SiteHeader hashMode={false} />

      <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
        <GridBg />
        <Orbs className="inset-0" />
        <div className="container mx-auto px-6 max-w-7xl relative grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
          <div className="text-center lg:text-right">
            <HeroBadge>Bulk SMS · اپراتورهای ایرانی</HeroBadge>
            <HeroTitle pre="کمپین پیامکی،" gradient="دقیق و قابل اندازه‌گیری" />
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-10 leading-relaxed">
              ارسال انبوه پیامک با سگمنت، A/B، شخصی‌سازی، خط اختصاصی و گزارش زنده — همه روی
              اپراتورهای ایرانی.
            </p>
            <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-10">
              <Link
                href="/auth"
                className="group w-full sm:w-auto px-8 py-4 rounded-full bg-foreground text-background font-bold flex items-center justify-center gap-2 shadow-2xl shadow-primary-main/20"
              >
                شارژ و ارسال <ArrowLeft size={18} />
              </Link>
              <Link
                href="/dashboard/sms"
                className="group flex items-center gap-3 text-foreground/75 hover:text-foreground"
              >
                <span className="w-12 h-12 rounded-full bg-foreground/5 border border-border grid place-items-center">
                  <PlayCircle size={18} />
                </span>
                <span className="text-sm font-medium">دموی پنل SMS</span>
              </Link>
            </div>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-7 gap-y-3 text-xs text-muted-foreground/80">
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={13} className="text-primary-main" /> %۹۸ تحویل
              </span>
              <span className="flex items-center gap-1.5">
                <Zap size={13} className="text-secondary-main" /> &lt;۳s ارسال
              </span>
            </div>
          </div>
          <SmsMock />
        </div>
      </section>

      <GiantStats
        items={[
          { value: "%۹۸", label: "نرخ تحویل" },
          { value: "<۳s", label: "میانگین تأخیر" },
          { value: "۳+", label: "اپراتور ایرانی" },
          { value: "+۱.۲M", label: "پیام در روز" },
        ]}
      />

      <Section>
        <SectionHeader eyebrow="قابلیت‌ها" title="هر چه برای کمپین حرفه‌ای لازم دارید" />
        <LineFeatures
          items={[
            {
              icon: Users,
              title: "سگمنت‌بندی پیشرفته",
              desc: "بر اساس تگ، خرید، فعالیت و سن مشتری.",
            },
            { icon: FileSpreadsheet, title: "Import مخاطب", desc: "از CSV، اکسل و CRM." },
            {
              icon: Sparkles,
              title: "شخصی‌سازی داینامیک",
              desc: "{name}، {city}، {last_order} و فیلدهای سفارشی.",
            },
            { icon: Calendar, title: "زمان‌بندی هوشمند", desc: "ارسال در بهترین ساعت هر گیرنده." },
            { icon: Target, title: "A/B تست", desc: "دو متن، دو سگمنت، برنده رو پیدا کن." },
            { icon: Filter, title: "بلک‌لیست خودکار", desc: "احترام به انصراف و قوانین." },
            { icon: Wallet, title: "کیف پول کسب‌وکار", desc: "شارژ متمرکز و فاکتور رسمی." },
            { icon: BarChart3, title: "گزارش زنده", desc: "تحویل، خطا، کلیک لینک کوتاه." },
          ]}
        />
      </Section>

      <Section className="border-t border-border">
        <SplitFeature
          eyebrow="گزارش زنده"
          title={
            <>
              هر کمپین تا <span className="brand-gradient-text">فاکتور</span> قابل پیگیری
            </>
          }
          desc="ترندی هر شمارهٔ پیامک رو با مشتری CRM وصل می‌کنه. ROI واقعی هر کمپین رو ببین."
          bullets={["نرخ تحویل و خطا زنده", "کلیک لینک کوتاه trd.ly", "نسبت‌دادن فروش به کمپین"]}
          slot={<CampaignStats />}
        />
      </Section>

      <Section className="border-t border-border">
        <SplitFeature
          reverse
          eyebrow="کیف پول"
          title={
            <>
              یک <span className="brand-gradient-text">کیف پول</span> برای کسب‌وکار
            </>
          }
          desc="شارژ متمرکز، انتقال بین حساب‌ها، فاکتور رسمی و تاریخچه‌ی کامل ارسال‌ها."
          bullets={["فاکتور رسمی و VAT", "هشدار شارژ کم", "انتقال بین workspaces"]}
          slot={<WalletMock />}
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="چطور ارسال کنم" title="در ۴ قدم تا اولین کمپین" />
        <StepsTimeline
          steps={[
            { title: "Import مخاطبین", desc: "اکسل، CSV، یا اتصال CRM." },
            { title: "ساخت سگمنت", desc: "فیلتر بر اساس تگ، رفتار، خرید." },
            { title: "نوشتن پیام", desc: "شخصی‌سازی + A/B تست." },
            { title: "زمان‌بند و ارسال", desc: "گزارش زنده ببین." },
          ]}
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="مقایسه" title="ترندی در برابر پنل‌های قدیمی" accent="secondary" />
        <CompareSplit
          ours={[
            "سگمنت‌بندی پیشرفته",
            "A/B تست بومی",
            "ROI تا فاکتور قابل پیگیری",
            "اپراتورهای ایرانی + مسیر بهینه",
            "شخصی‌سازی داینامیک قدرتمند",
          ]}
          theirs={[
            "ارسال جمعی به همه",
            "بدون A/B",
            "گزارش فقط ارسال/خطا",
            "اپراتور ثابت",
            "{name} ساده، بدون فیلد سفارشی",
          ]}
        />
      </Section>

      <Section>
        <BigQuote
          quote="نرخ تبدیل کمپین ۲ برابر پنل قبلی شد. گزارش‌های ROI خیال ما رو راحت کرد."
          name="محمد جلالی"
          role="فروشگاه باران"
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="مشتریان" title="برندهایی که با ترندی پیامک می‌فرستند" />
        <MiniTestimonials
          items={[
            {
              name: "Niloofar A.",
              role: "Beauty Brand",
              quote: "Personalization actually works for SMS too.",
            },
            {
              name: "حمیدرضا ر.",
              role: "آژانس بازاریابی",
              quote: "گزارش‌های ROI خیال ما رو راحت کرد.",
            },
            {
              name: "نگار م.",
              role: "املاک پارس",
              quote: "خط اختصاصی + کیف پول کسب‌وکار = راحتی کامل.",
            },
          ]}
        />
      </Section>

      <Section>
        <SectionHeader eyebrow="پرسش‌ها" title="سؤالات متداول" />
        <FAQ
          items={[
            {
              q: "از چه اپراتوری استفاده می‌کنید؟",
              a: "ایرانسل، همراه اول و رایتل با مسیر بهینه خودکار.",
            },
            { q: "خط اختصاصی می‌دید؟", a: "بله، خطوط خدماتی و تبلیغاتی قابل خرید است." },
            { q: "اگر مشتری انصراف داد چه؟", a: "خودکار بلک‌لیست می‌شه و دیگه پیام نمی‌گیره." },
          ]}
        />
      </Section>

      <PricingStrip />
      <FinalCTA
        title={
          <>
            اولین کمپین <span className="brand-gradient-text">پیامکی</span> رو امروز بزن
          </>
        }
        desc="شارژ سریع، ارسال زیر ۳ ثانیه."
      />
      <SiteFooter />
    </SolutionShell>
  );
}

function SmsMock() {
  const msgs = [
    { txt: "سلام علی 👋 ۲۰٪ تخفیف ویژه‌ت آماده‌ست → trd.ly/x9", t: "الان" },
    { txt: "🎁 کد off20 — فقط تا فردا", t: "۱ دق پیش" },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative grid gap-4"
    >
      <div className="absolute -inset-6 rounded-[3rem] bg-gradient-to-br from-primary-main/30 to-secondary-main/30 blur-3xl opacity-50 -z-10" />
      <div className="rounded-3xl border border-border bg-background p-5 shadow-xl">
        <div className="flex items-center gap-2 text-sm font-black text-foreground mb-3">
          <Send size={16} className="text-primary-main" /> کمپین #تخفیف_ویژه
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          <Mini v="۱۲٬۸۴۰" l="ارسال" />
          <Mini v="۹۸٪" l="تحویل" />
          <Mini v="۲٬۴۲۰" l="کلیک لینک" />
        </div>
        <div className="h-2 rounded-full bg-foreground/[0.06] overflow-hidden">
          <div
            className="h-full bg-gradient-to-l from-primary-main to-secondary-main"
            style={{ width: "78%" }}
          />
        </div>
        <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
          <span>۷۸٪ تکمیل</span>
          <span className="flex items-center gap-1">
            <Clock size={11} /> ۲ دقیقه باقی
          </span>
        </div>
      </div>
      <div className="rounded-3xl border-[10px] border-border bg-background overflow-hidden max-w-[280px] mx-auto">
        <div className="bg-foreground/[0.05] px-4 py-2 text-[10px] text-muted-foreground border-b border-border">
          پیامک · ۹۸۰۰۰۰۲۰۲۰
        </div>
        <div className="p-3 space-y-2 min-h-[200px]">
          {msgs.map((m, i) => (
            <div
              key={i}
              className="bg-primary-main/15 border border-primary-main/30 text-foreground text-xs px-3 py-2 rounded-2xl rounded-tl-sm"
            >
              {m.txt}
              <div className="text-[9px] text-muted-foreground mt-1">{m.t}</div>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}
function Mini({ v, l }: { v: string; l: string }) {
  return (
    <div className="rounded-xl bg-foreground/[0.04] border border-border/60 p-2 text-center">
      <div className="text-sm font-black text-foreground">{v}</div>
      <div className="text-[9px] text-muted-foreground">{l}</div>
    </div>
  );
}

function CampaignStats() {
  return (
    <div className="rounded-3xl border border-border bg-foreground/[0.04] p-6">
      <div className="flex items-center gap-2 text-sm font-black text-foreground mb-4">
        <TrendingUp size={16} className="text-secondary-main" /> ROI کمپین
      </div>
      {[
        { l: "ارسال شده", v: "۱۲٬۸۴۰", c: "primary" },
        { l: "تحویل", v: "۱۲٬۵۸۳", c: "primary" },
        { l: "کلیک لینک", v: "۲٬۴۲۰", c: "secondary" },
        { l: "خرید", v: "۴۸۲", c: "secondary" },
        { l: "درآمد", v: "۲۸۴M ت", c: "primary" },
      ].map((r) => (
        <div
          key={r.l}
          className="flex items-center justify-between py-2.5 border-b border-border/60 last:border-0"
        >
          <span className="text-xs text-muted-foreground">{r.l}</span>
          <span
            className={`text-sm font-black ${r.c === "primary" ? "text-primary-main" : "text-secondary-main"}`}
          >
            {r.v}
          </span>
        </div>
      ))}
    </div>
  );
}

function WalletMock() {
  return (
    <div className="rounded-3xl border border-border bg-foreground/[0.04] p-6">
      <div className="flex items-center gap-2 text-sm font-black text-foreground mb-4">
        <Wallet size={16} className="text-primary-main" /> کیف پول
      </div>
      <div className="rounded-2xl bg-gradient-to-br from-primary-main to-secondary-main p-5 mb-4 text-foreground">
        <div className="text-xs opacity-80">موجودی فعلی</div>
        <div className="text-3xl font-black mt-1">
          ۲٬۴۸۰٬۰۰۰ <span className="text-sm opacity-80">تومان</span>
        </div>
        <div className="flex items-center gap-2 mt-3 text-[11px] opacity-80">
          <Tag size={11} /> ~ ۱۲٬۴۰۰ پیامک
        </div>
      </div>
      {[
        { d: "خرید SMS Pack", a: "+۵۰۰٬۰۰۰", c: "+" },
        { d: "کمپین #تخفیف", a: "−۲۸۰٬۰۰۰", c: "-" },
        { d: "خرید SMS Pack", a: "+۱٬۰۰۰٬۰۰۰", c: "+" },
      ].map((t, i) => (
        <div
          key={i}
          className="flex items-center justify-between py-2 border-b border-border/60 last:border-0 text-xs"
        >
          <span className="text-foreground/75">{t.d}</span>
          <span
            className={`font-black ${t.c === "+" ? "text-primary-main" : "text-secondary-main"}`}
          >
            {t.a}
          </span>
        </div>
      ))}
    </div>
  );
}

export default Page;
